import NearbyModel from './nearby-model';
import NearbyView from './nearby-view';
import * as UIService from '../../services/ui-service';
import AuthService from '../../services/auth-service';
import LocalStorage from '../../data/local-storage';

export default class NearbyPresenter {
  constructor(model, view, appInterface) {
    this.model = model || new NearbyModel();
    this.view = view || new NearbyView();
    this.appInterface = appInterface || {};
  }

  async init() {
    this.model.loadUserState();

    if (!this.model.isAuthenticated) {
      this.showGuestMode();
      return;
    }

    await this.initNearbyMap();
    await this.loadNearbyStories();
    this.setupEventListeners();
  }

  showGuestMode() {
    this.view.showGuestMapPlaceholder('#nearby-map');
    this.view.showAuthRequired('#nearby-posts-container', () =>
      this.appInterface.navigateTo('auth')
    );
  }

  async initNearbyMap() {
    try {
      const isDark = this.view.isDarkMode();
      const config = this.model.getMapConfig(isDark);

      this.view.initializeMap(config);
      this.view.addMapLayerControl((layerType) => {
        this.changeMapLayer(layerType);
      }, this.model.getCurrentMapLayer());

      this.view.updateLayerSelection(this.model.getCurrentMapLayer());

      try {
        const location = await this.model.getUserLocation();
        if (location) {
          this.model.setCurrentUserLocation(location);
          this.view.setMapCenter(location.lon, location.lat, 10);
          this.view.addUserLocationMarker(location);
          this.addRangeCircleToMap();
        }
      } catch (error) {}
    } catch (error) {
      this.view.showMapError('#nearby-map', 'Gagal memuat peta');
    }
  }

  addRangeCircleToMap() {
    const circleData = this.model.getRangeCircleData(this.model.nearbyDistance);
    if (circleData) {
      this.view.addRangeCircle(circleData);
    }
  }

  async loadNearbyStories() {
    try {
      this.view.showLoading('#nearby-posts-container');

      const allStories = await this.model.getAllStoriesWithLocation();
      const nearbyStories = this.model.filterStoriesByDistance(allStories);

      this.model.setNearbyStories(nearbyStories);

      if (nearbyStories.length > 0) {
        const storiesWithBookmarkStatus = this.addBookmarkStatusToStories(nearbyStories);
        this.view.renderStories(
          storiesWithBookmarkStatus,
          '#nearby-posts-container',
          this.model.currentUserLocation
        );

        setTimeout(() => {
          this.view.addStoryMarkers(nearbyStories);
        }, 500);

        await this.loadLocationNames(nearbyStories);
        if (this.appInterface.reinitTooltips)
          this.appInterface.reinitTooltips();
      } else {
        const distance = this.model.nearbyDistance;
        const emptyMessage = `Belum ada cerita dalam radius ${distance} km dari lokasi Anda. Silakan perbesar radius untuk melihat postingan dengan radius lebih besar.`;
        this.view.showEmptyState('#nearby-posts-container', emptyMessage);
      }
    } catch (error) {
      if (error.message.includes('401')) {
        this.view.showAuthRequired('#nearby-posts-container', () =>
          this.appInterface.navigateTo('auth')
        );
      } else {
        this.view.showEmptyState('#nearby-posts-container', 'Gagal memuat cerita terdekat');
      }
    }
  }

  addBookmarkStatusToStories(stories) {
    return stories.map((story) => ({
      ...story,
      isBookmarked: this.model.isBookmarked(story.id),
    }));
  }

  async loadNearbyStories() {
    try {
      this.view.showLoading('#nearby-posts-container');

      // Gunakan method dedicated untuk nearby page
      const allStories = await this.model.getAllNearbyStories();
      const nearbyStories = this.model.filterStoriesByDistance(allStories);

      this.model.setNearbyStories(nearbyStories);

      if (nearbyStories.length > 0) {
        const storiesWithBookmarkStatus = this.addBookmarkStatusToStories(nearbyStories);
        this.view.renderStories(
          storiesWithBookmarkStatus,
          '#nearby-posts-container',
          this.model.currentUserLocation
        );

        setTimeout(() => {
          this.view.addStoryMarkers(nearbyStories);
        }, 500);

        await this.loadLocationNames(nearbyStories);
      } else {
        const distance = this.model.nearbyDistance;
        const emptyMessage = `Belum ada cerita dalam radius ${distance} km dari lokasi Anda. Silakan perbesar radius untuk melihat postingan dengan radius lebih besar.`;
        this.view.showEmptyState('#nearby-posts-container', emptyMessage);
      }
    } catch (error) {
      if (error.message.includes('401')) {
        this.view.showAuthRequired('#nearby-posts-container', () =>
          this.appInterface.navigateTo('auth')
        );
      } else {
        this.view.showEmptyState('#nearby-posts-container', 'Gagal memuat cerita terdekat');
      }
    }
  }

  async loadLocationNames(stories) {
    for (const story of stories) {
      if (story.lat && story.lon) {
        try {
          const locationName = await this.model.getLocationName(story.lat, story.lon);
          this.view.updateLocationName(story.id, locationName);
        } catch (error) {
          this.view.updateLocationName(story.id, 'Lokasi tidak tersedia');
        }
      }
    }
  }

  async loadSidebarLocationNames(stories) {
    for (const story of stories) {
      if (story.lat && story.lon) {
        try {
          const locationName = await this.model.getLocationName(story.lat, story.lon);
          const distance = this.model.currentUserLocation
            ? this.model.calculateDistance(
                this.model.currentUserLocation.lat,
                this.model.currentUserLocation.lon,
                story.lat,
                story.lon
              )
            : null;
          this.view.updateSidebarLocationName(story.id, locationName, distance);
        } catch (error) {
          this.view.updateSidebarLocationName(story.id, 'Lokasi tidak tersedia');
        }
      }
    }
  }

  setupEventListeners() {
    this.view.setupPostInteractionEventListeners(
      (storyId) => this.toggleBookmark(storyId),
      (storyId) => this.sharePost(storyId),
      (src) => this.showLightbox(src),
      (id) => this.appInterface.navigateToPost(id)
    );
  }

  toggleBookmark(storyId) {
    if (!this.model.isAuthenticated) {
      this.appInterface.showToast('Masuk terlebih dahulu untuk menyimpan postingan', 'warning');
      return;
    }

    const isCurrentlyBookmarked = this.model.isBookmarked(storyId);

    if (isCurrentlyBookmarked) {
      this.removeBookmark(storyId);
    } else {
      this.addBookmark(storyId);
    }
  }

  addBookmark(storyId) {
    const bookmark = this.model.addBookmark(storyId);

    if (bookmark) {
      this.updateGlobalBookmarks();
      this.view.updateBookmarkStatus(storyId, true);
      this.appInterface.showToast('Postingan berhasil disimpan', 'success');
    }
  }

  removeBookmark(storyId) {
    const success = this.model.removeBookmark(storyId);

    if (success) {
      this.updateGlobalBookmarks();
      this.view.updateBookmarkStatus(storyId, false);
      this.appInterface.showToast('Postingan dihapus dari bookmark', 'success');
    }
  }

  sharePost(storyId) {
    const baseUrl = this.appInterface.getCurrentUrl();
    const url = `${baseUrl}#/share/${storyId}`;
    this.appInterface.setShareUrl(url);
    this.appInterface.showDialog('share-dialog');
  }

  showLightbox(src) {
    this.appInterface.showLightbox(src);
  }

  updateDistance(newDistance) {
    this.model.updateDistance(newDistance);

    this.loadNearbyStoriesOnly();
    this.updateRangeCircleOnly();
    this.loadSidebarUsers();
  }

  changeMapLayer(layerType) {
    const isDark = this.view.isDarkMode();
    const styleUrl = this.model.getMapStyleConfig(isDark, layerType);
    this.view.setMapStyle(styleUrl, () => {
      if (this.model.currentUserLocation) {
        this.view.addUserLocationMarker(this.model.currentUserLocation);
      }
      this.addRangeCircleToMap();
      this.view.addStoryMarkers(this.model.nearbyStories);
    });
    this.model.setCurrentMapLayer(layerType);
    this.view.updateLayerSelection(layerType);
  }

  applyExternalMapLayer(layerType) {
    if (this.model.currentMapLayer === layerType) return;
    const isDark = this.view.isDarkMode();
    const styleUrl = this.model.getMapStyleConfig(isDark, layerType);
    this.view.setMapStyle(styleUrl, () => {
      if (this.model.currentUserLocation) {
        this.view.addUserLocationMarker(this.model.currentUserLocation);
      }
      this.addRangeCircleToMap();
      this.view.addStoryMarkers(this.model.nearbyStories);
    });
    this.model.currentMapLayer = layerType;
    this.view.updateLayerSelection(layerType);
  }

  async loadNearbyStoriesOnly() {
    try {
      // Load stories tanpa init ulang map - gunakan method dedicated
      const allStories = await this.model.getAllNearbyStories();
      const nearbyStories = this.model.filterStoriesByDistance(allStories);
      this.model.setNearbyStories(nearbyStories);

      if (nearbyStories.length > 0) {
        const storiesWithBookmarkStatus = this.addBookmarkStatusToStories(nearbyStories);
        this.view.renderStories(
          storiesWithBookmarkStatus,
          '#nearby-posts-container',
          this.model.currentUserLocation
        );
        this.view.addStoryMarkers(nearbyStories);
        await this.loadLocationNames(nearbyStories);
        if (this.appInterface.reinitTooltips)
          this.appInterface.reinitTooltips();
      } else {
        const distance = this.model.nearbyDistance;
        const emptyMessage = `Belum ada cerita dalam radius ${distance} km dari lokasi Anda. Silakan perbesar radius untuk melihat postingan dengan radius lebih besar.`;
        this.view.showEmptyState('#nearby-posts-container', emptyMessage);
        this.view.clearStoryMarkers();
      }
    } catch (error) {
      this.view.showEmptyState('#nearby-posts-container', 'Gagal memuat cerita terdekat');
    }
  }

  updateRangeCircleOnly() {
    const circleData = this.model.getRangeCircleData(this.model.nearbyDistance);
    if (circleData) {
      this.view.updateRangeCircle(circleData);
    }
  }

  updateGlobalBookmarks() {
    this.appInterface.setBookmarks([...this.model.bookmarks]);
    this.appInterface.updateBookmarkBubble();
  }

  async loadSidebarUsers() {
    if (!this.model.isAuthenticated) {
      this.view.clearSidebarUsers();
      return;
    }

    this.view.showSidebarLoading();

    try {
      const nearbyUsers = await this.model.loadNearbyUsersData(
        this.model.currentUserLocation,
        this.model.nearbyDistance
      );

      this.view.renderSidebarUsers(
        nearbyUsers,
        this.model.currentUserLocation,
        () => this.appInterface.toggleDistanceDialog && this.appInterface.toggleDistanceDialog()
      );
      await this.loadSidebarLocationNames(nearbyUsers);
      if (this.appInterface.reinitTooltips)
        this.appInterface.reinitTooltips();
    } catch (error) {
      this.view.showSidebarError(
        this.model.nearbyDistance,
        () => this.appInterface.toggleDistanceDialog && this.appInterface.toggleDistanceDialog()
      );
    }
  }

  cleanup() {
    this.view.cleanup();
  }

  refresh() {
    this.loadNearbyStories();
  }
}
